import {
  faBell,
  faBellRing,
  faBellSlash
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import Pusher from 'pusher-js'
import {
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDeleteNotificationMutation,
  useReadAllNotifications,
  useReadNotificationMutation
} from '~/api/mutations/user'
import { applyPathViewedActivity } from '~/api/queries/pathViewed'
import {
  type RealTimeUserNotification,
  useCurrentUserNotifications,
  useNextEvent
} from '~/api/queries/user'
import { API_ENDPOINT } from '~/api/utils'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { container } from '~/components/Global/Interactions/Dropdown/Downdown.css'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import {
  toolBarItemAttention,
  toolBarItemAttentionCount
} from '~/components/Layout/Header/Header.css'
import type { NotificationsViewerProps } from '~/components/Layout/Header/Notification/common'
import { FlashNotification } from '~/components/Layout/Header/Notification/FlashNotification'
import { Notification } from '~/components/Layout/Header/Notification/Notification'
import {
  notificationDropdown,
  notificationRTStack,
  notificationStack,
  notificationsClearButton,
  notificationsTitle,
  notificationsTitleCount,
  notificationZone
} from '~/components/Layout/Header/Notification/Notifications.css'
import { useConfig } from '~/domains/configuration/queries/config'
import { subscribeToGlobalMessages } from '~/domains/globalMessages/subscribeToGlobalMessages'
import { orderShortlistFeedbacksQueryKey } from '~/domains/orders/queries/orderShortlistFeedbacks'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { AuthContext } from '~/utils/contextes'

const bellAnimation: Keyframe[] = [
  { transform: 'rotate(10deg)' },
  { transform: 'rotate(-10deg)' }
]
const bellAnimationOptions: KeyframeAnimationOptions = {
  duration: 300,
  iterations: 20,
  direction: 'alternate',
  easing: 'linear'
}

export const NotificationsViewer = ({
  classname,
  currentUserId,
  defaultCount
}: NotificationsViewerProps): ReactElement => {
  const queryClient = useQueryClient()
  const [isOpen, setOpen] = useState<boolean>(false)
  const [count, setCount] = useState<number>(defaultCount ?? 0)
  const [realtimeNotifications, setRealtimeNotifications] = useState<
    RealTimeUserNotification[]
  >([])
  const [lastSlugsSeen, setLastSlugsSeen] = useState<
    {
      slug: string
      seenAt: Date
    }[]
  >([])
  const dimissEventLocal = localStorage.getItem('dismissEvent')
  const currentUser = useContext(AuthContext)
  const triggerRef = useRef<HTMLDivElement>(null)
  const notificationBellRef = useRef<SVGSVGElement>(null)
  const [dismissedEventId, setDismissedEventId] = useState<number | null>(
    dimissEventLocal ? Number.parseInt(dimissEventLocal, 10) : null
  )
  const userId = Number(currentUser?.id ?? currentUserId)
  const readMutation = useReadNotificationMutation({
    userId
  })
  const deleteMutation = useDeleteNotificationMutation({
    userId
  })
  const {
    userNotifications,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    isError,
    fetchNextPage
  } = useCurrentUserNotifications({
    userId,
    active: isOpen
  })

  const { data: nextEventData } = useNextEvent({ userId, dismissedEventId })

  const clearNotificationsMutation = useReadAllNotifications({
    userId
  })

  useEffect(() => {
    if (
      defaultCount !== undefined &&
      defaultCount > 0 &&
      notificationBellRef.current
    ) {
      notificationBellRef.current.animate(bellAnimation, bellAnimationOptions)
    }
  }, [defaultCount])

  useEffect(() => {
    const documentTitle = document.title.replace(/^\(\d+\) /, '')
    if (count > 0) {
      document.title = `(${count}) ${documentTitle}`
    } else {
      document.title = documentTitle
    }
  }, [count])

  const { data: instanceName } = useConfig('organization.slug')
  const { data: pusherKey } = useConfig('organization.pusher_key')

  useEffect(() => {
    if (!pusherKey) {
      return
    }
    let pusher: Pusher
    if (window.JobConfig === undefined) {
      pusher = new Pusher(pusherKey, {
        cluster: 'eu',
        channelAuthorization: {
          endpoint: `${API_ENDPOINT}/v1/users/${userId}/pusher_auth`,
          transport: 'ajax',
          headers: {
            'x-JWT': localStorage.getItem('token') ?? '',
            'x-INSTANCE': localStorage.getItem('instance') ?? ''
          }
        }
      })
    } else {
      pusher = new Pusher(pusherKey, {
        cluster: 'eu',
        authEndpoint: '/accueil/pusher_auth'
      })
    }
    subscribeToGlobalMessages(pusher, queryClient)
    const channel = pusher.subscribe(`private-${instanceName}-user-${userId}`)
    channel.bind('path-viewed', (rawData: string) => {
      applyPathViewedActivity(queryClient, JSON.parse(rawData))
    })
    channel.bind('notification', (rawData: any) => {
      const notification: RealTimeUserNotification = JSON.parse(rawData)
      setCount(notification.unreadCount)
      void queryClient.invalidateQueries({
        queryKey: ['user', 'notifications', userId]
      })
      if (notification.type === 'shortlist_submitted') {
        void queryClient.invalidateQueries({
          queryKey: orderShortlistFeedbacksQueryKey(
            notification.metadata.details.orderId
          )
        })
      }

      // Animate bell
      if (notificationBellRef.current) {
        notificationBellRef.current.animate(bellAnimation, bellAnimationOptions)
      }

      // Check seen slugs in last 15 minutes to avoid flooding
      const lastSeenSlugsGC = lastSlugsSeen.filter(
        (s) => s.seenAt.getTime() > Date.now() - 15 * 60 * 1000
      )
      if (lastSeenSlugsGC.find((n) => n.slug === notification.slug)) {
        return
      }
      setLastSlugsSeen(() => [
        ...lastSeenSlugsGC,
        { slug: notification.slug, seenAt: new Date() }
      ])
      setRealtimeNotifications((prev) => [...prev, notification])
    })
    return () => {
      pusher.disconnect()
    }
  }, [lastSlugsSeen, queryClient, userId, pusherKey, instanceName])

  const endOfListReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached
  })

  const onCloseFlashNotification = useCallback((slug: string) => {
    setRealtimeNotifications((prev) => prev.filter((n) => n.slug !== slug))
  }, [])

  const handleClearNotifications = () => {
    if (!clearNotificationsMutation.isPending) {
      setCount(0)
      clearNotificationsMutation.mutate()
    }
  }

  const nextEvent =
    nextEventData &&
    new Date(nextEventData.date) <= new Date(Date.now() + 15 * 60 * 1000)
      ? nextEventData
      : null

  const unReadUserNotifications =
    userNotifications?.filter((notification) => !notification.isRead) ?? []

  const notSoonNotifications = (userNotifications ?? []).filter(
    (notification) => notification.type !== 'soon_event'
  )

  return (
    <>
      <div
        className={clsx(
          classname,
          container,
          count > 0 ? toolBarItemAttention : null
        )}
        ref={triggerRef}
        onClick={() => {
          setOpen(!isOpen)
        }}
      >
        {count > 0 && (
          <span className={toolBarItemAttentionCount}>{count}</span>
        )}
        <FontAwesomeIcon
          icon={count > 0 ? faBellRing : faBell}
          ref={notificationBellRef}
        />
      </div>
      <div className={notificationZone}>
        <Dropdown
          isOpen={isOpen}
          onclose={() => setOpen(false)}
          showCloseTrigger={true}
          dropdownRef={triggerRef}
          className={notificationDropdown}
          offsetValue={25}
        >
          <div className={notificationsTitle}>
            Notifications{' '}
            <strong className={notificationsTitleCount}>
              ({Math.max(count, 0)})
            </strong>
            {unReadUserNotifications.length > 0 && (
              <span
                className={notificationsClearButton}
                onClick={handleClearNotifications}
              >
                {!clearNotificationsMutation.isPending
                  ? 'Tout marquer comme lu'
                  : ''}
              </span>
            )}
          </div>
          {userNotifications?.length === 0 && !isError && (
            <NoResult
              message="Aucune notification"
              isSmall
              icon={faBellSlash}
            />
          )}
          <AnimatePresence>
            <motion.div
              className={notificationStack}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
              initial="hidden"
              animate="visible"
            >
              {notSoonNotifications.map((notification) => {
                return (
                  <Notification
                    key={notification.id}
                    notification={notification}
                    onNotificationRead={() => {
                      if (!notification.isRead) {
                        setCount((count) => count - 1)
                        readMutation.mutate({
                          notificationId: notification.id
                        })
                      }
                    }}
                    onNotificationDelete={() => {
                      if (!notification.isRead) {
                        setCount((count) => count - 1)
                      }
                      deleteMutation.mutate({
                        notificationId: notification.id
                      })
                    }}
                    onNotificationOpen={() => setOpen(false)}
                  />
                )
              })}
            </motion.div>
          </AnimatePresence>
          {isLoading && !isError && <Loader small={true} />}
          {isError && (
            <Alert
              variation={AlertVariation.Danger}
              message="Une erreur est survenue lors de la récupération des notifications"
              onDismiss={false}
            />
          )}
          <div ref={lastItemRef} />
        </Dropdown>

        <div className={notificationRTStack}>
          <AnimatePresence>
            {nextEvent && dismissedEventId !== nextEvent.id && (
              <FlashNotification
                key={nextEvent.id}
                notification={{
                  id: nextEvent.id,
                  slug: `soon_event_${nextEvent.id}`,
                  type: 'soon_event',
                  unreadCount: 0,
                  metadata: {
                    details: {
                      type: nextEvent.type,
                      readableType: nextEvent.readableType,
                      meetingLink: nextEvent.meetingLink,
                      link: nextEvent.link,
                      date: nextEvent.date,
                      recipients: nextEvent.recipients
                    }
                  }
                }}
                onClose={() => {
                  setDismissedEventId(nextEvent.id)
                  localStorage.setItem('dismissEvent', nextEvent.id.toString())
                }}
              />
            )}

            {/* Realtime notifications */}
            {realtimeNotifications.map((notification) => (
              <FlashNotification
                key={notification.slug}
                notification={notification}
                onClose={() => {
                  readMutation.mutate({
                    notificationId: notification.id
                  })
                  setCount((count) => count - 1)
                  onCloseFlashNotification(notification.slug)
                }}
              />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </>
  )
}
